/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './subjectnotes.css';

const Icons = {
    Star: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 9 22 9 16.5 13.5 18.8 21 12 16.8 5.2 21 7.5 13.5 2 9 9 9 12 2"></polygon></svg>,
    Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Calendar: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    Clock: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>,
    Filter: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>,
    Link: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
};

export default function Notes() {
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState([]);
    const [filteredNotes, setFilteredNotes] = useState([]);
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'overdue', 'approaching', 'done'
    const [filterImportant, setFilterImportant] = useState(false);
    const [sortBy, setSortBy] = useState('created_at');
    const [error, setError] = useState('');

    useEffect(() => {
        loadNotes();
    }, []);

    const loadNotes = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/fetch_all_notes.php`,
                { params: { sort_by: 'created_at', sort_order: 'DESC' } }
            );
            if (response.data.success) {
                setNotes(response.data.notes || []);
            } else {
                setError(response.data.error || 'Failed to load notes');
                setNotes([]);
            }
        } catch (err) {
            setError('Failed to load notes');
            setNotes([]);
        } finally {
            setLoading(false);
        }
    };

    // Apply filters and sorting
    useEffect(() => {
        let filtered = [...notes];

        // Filter by status
        if (filterStatus !== 'all') {
            if (filterStatus === 'done') {
                filtered = filtered.filter(n => n.is_done === 1);
            } else {
                filtered = filtered.filter(n => n.status === filterStatus && n.is_done === 0);
            }
        }

        // Filter by important
        if (filterImportant) {
            filtered = filtered.filter(n => n.is_important === 1);
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'due_date':
                    if (!a.due_date && !b.due_date) return 0;
                    if (!a.due_date) return 1;
                    if (!b.due_date) return -1;
                    return new Date(a.due_date) - new Date(b.due_date);
                case 'subject':
                    return (a.subject_name || '').localeCompare(b.subject_name || '');
                default:
                    return new Date(b.created_at) - new Date(a.created_at);
            }
        });

        setFilteredNotes(filtered);
    }, [notes, filterStatus, filterImportant, sortBy]);

    const toggleNoteStatus = async (noteId, currentStatus) => {
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'update',
                note_id: noteId,
                is_done: currentStatus === 1 ? 0 : 1
            });
            loadNotes();
        } catch (error) {
            alert('Failed to update note');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'No due date';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusBadgeColor = (status, isDone) => {
        if (isDone) return '#28a745';
        if (status === 'overdue') return '#dc3545';
        if (status === 'approaching') return '#ffc107';
        return '#6c757d';
    };

    const getStatusLabel = (status, isDone) => {
        if (isDone) return 'Completed';
        if (status === 'overdue') return 'Overdue';
        if (status === 'approaching') return 'Approaching';
        return 'Future';
    };

    if (loading) {
        return <div className="loading_state">Loading notes...</div>;
    }

    return (
        <div className="subject-notes-page">
            <div style={{ marginBottom: '30px' }}>
                <h1 style={{ marginBottom: '10px', color: '#333' }}>All Notes</h1>
                <p style={{ color: '#666', fontSize: '0.95em' }}>
                    {filteredNotes.length} of {notes.length} notes
                </p>
            </div>

            {error && (
                <div style={{
                    padding: '15px',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    color: '#721c24',
                    marginBottom: '20px'
                }}>
                    {error}
                </div>
            )}

            {/* Filters and Sort */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px',
                marginBottom: '25px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
            }}>
                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '0.9em', color: '#495057' }}>
                        <Icons.Filter /> Status
                    </label>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            fontSize: '0.9em'
                        }}
                    >
                        <option value="all">All Notes</option>
                        <option value="overdue">Overdue</option>
                        <option value="approaching">Approaching</option>
                        <option value="future">Future</option>
                        <option value="done">Completed</option>
                    </select>
                </div>

                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '0.9em', color: '#495057' }}>
                        <Icons.Star /> Sort By
                    </label>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            fontSize: '0.9em'
                        }}
                    >
                        <option value="created_at">Recently Created</option>
                        <option value="due_date">Due Date</option>
                        <option value="subject">Subject</option>
                    </select>
                </div>

                <div>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginTop: '22px' }}>
                        <input
                            type="checkbox"
                            checked={filterImportant}
                            onChange={(e) => setFilterImportant(e.target.checked)}
                            style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9em', color: '#495057' }}>Important Only</span>
                    </label>
                </div>
            </div>

            {/* Notes List */}
            {filteredNotes.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    color: '#6c757d'
                }}>
                    <p style={{ fontSize: '1.1em', marginBottom: '10px' }}>No notes found</p>
                    <p style={{ fontSize: '0.9em' }}>Try adjusting your filters</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                    {filteredNotes.map((note) => (
                        <div
                            key={note.id}
                            style={{
                                backgroundColor: 'white',
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                padding: '16px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s',
                                opacity: note.is_done ? 0.7 : 1
                            }}
                        >
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                {/* Checkbox */}
                                <button
                                    onClick={() => toggleNoteStatus(note.id, note.is_done)}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        minWidth: '24px',
                                        border: `2px solid ${getStatusBadgeColor(note.status, note.is_done)}`,
                                        borderRadius: '4px',
                                        backgroundColor: note.is_done ? getStatusBadgeColor(note.status, note.is_done) : 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        marginTop: '2px'
                                    }}
                                >
                                    {note.is_done && <Icons.Check />}
                                </button>

                                {/* Note Content */}
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                        <span style={{
                                            fontWeight: '600',
                                            fontSize: '0.85em',
                                            backgroundColor: getStatusBadgeColor(note.status, note.is_done),
                                            color: 'white',
                                            padding: '4px 10px',
                                            borderRadius: '4px'
                                        }}>
                                            {getStatusLabel(note.status, note.is_done)}
                                        </span>
                                        {note.is_important === 1 && (
                                            <span style={{ color: '#ffc107', fontSize: '0.95em' }}>
                                                <Icons.Star /> Important
                                            </span>
                                        )}
                                    </div>

                                    <h3 style={{
                                        margin: '0 0 8px 0',
                                        fontSize: '1.05em',
                                        color: '#333',
                                        textDecoration: note.is_done ? 'line-through' : 'none'
                                    }}>
                                        {note.note_text}
                                    </h3>

                                    <div style={{ display: 'flex', gap: '20px', fontSize: '0.85em', color: '#6c757d', marginTop: '10px' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Icons.Link />
                                            <strong>{note.subject_name}</strong>
                                        </span>

                                        {note.due_date && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Icons.Calendar />
                                                {formatDate(note.due_date)}
                                            </span>
                                        )}

                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Icons.Clock />
                                            Created {formatTime(note.created_at)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
